import { useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import {
  Play,
  Image as ImageIcon,
  Sparkles,
  Copy,
  Check,
  Gift,
  Crown,
  Zap,
  ArrowRight,
  Music
} from "lucide-react";
import { toast } from "sonner";

const Example = () => {
  const { t } = useLanguage();
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);

  const videoExamples = [
    {
      id: 1,
      model: "Veo 3",
      title: t("Veo 3 ตัวอย่าง", "Veo 3 Example"),
      video: "/example/veo3exam1.mp4",
      prompt: "Cinematic high quality video generated with Google Veo 3, professional lighting and color grading, smooth motion, 4K quality",
      duration: "10s",
      credits: 60
    },
    {
      id: 2,
      model: "Sora 2",
      title: t("Sora 2 ตัวอย่าง 1", "Sora 2 Example 1"),
      video: "/example/example sora1.mp4",
      prompt: "High quality AI generated video with OpenAI Sora 2, cinematic style, professional production quality",
      duration: "10s",
      credits: 30
    },
    {
      id: 3,
      model: "Sora 2",
      title: t("Sora 2 ตัวอย่าง 2", "Sora 2 Example 2"),
      video: "/example/example sora2.mp4",
      prompt: "Creative AI video generation with Sora 2, smooth transitions, professional cinematography",
      duration: "10s",
      credits: 30
    },
    {
      id: 4,
      model: "Sora 2",
      title: t("Sora 2 ตัวอย่าง 3", "Sora 2 Example 3"),
      video: "/example/example sora3.mp4",
      prompt: "Stunning AI generated content with OpenAI Sora 2, dynamic scenes, high production value",
      duration: "10s",
      credits: 30
    }
  ];

  const imageExamples = [
    {
      id: 1,
      model: "Seedream 4.5",
      title: t("Seedream ตัวอย่าง 1", "Seedream Example 1"),
      image: "/example/seedream4.5.jpg",
      prompt: "High quality AI generated image with Seedream 4.5, photorealistic details, professional lighting, stunning composition",
      credits: 3.5
    },
    {
      id: 2,
      model: "Seedream 4.5",
      title: t("Seedream ตัวอย่าง 2", "Seedream Example 2"),
      image: "/example/seedream4.5-2.jpeg",
      prompt: "Creative AI image generation with Seedream 4.5, beautiful aesthetics, high resolution output",
      credits: 3.5
    },
    {
      id: 3,
      model: "Seedream 4.5",
      title: t("Seedream ตัวอย่าง 3", "Seedream Example 3"),
      image: "/example/seedream4.5-3.jpg",
      prompt: "Artistic AI generated content with Seedream 4.5, vibrant colors, professional quality",
      credits: 3.5
    },
    {
      id: 4,
      model: "Seedream 4.5",
      title: t("Seedream ตัวอย่าง 4", "Seedream Example 4"),
      image: "/example/seedream4.5-2-4.jpg",
      prompt: "Stunning visual creation with Seedream 4.5, detailed textures, cinematic style",
      credits: 3.5
    },
    {
      id: 5,
      model: "Gemini Banana",
      title: t("Gemini Banana ตัวอย่าง", "Gemini Banana Example"),
      image: "/example/google nano banana.png",
      prompt: "Creative AI image generated with Google Gemini Banana model, unique artistic style, high quality output",
      credits: 4
    }
  ];

  const musicExamples = [
    {
      id: 1,
      style: "Pop",
      title: t("รักในฤดูร้อน", "Summer Love"),
      thumbnail: "https://images.unsplash.com/photo-1514525253361-bee87387343b?w=800",
      prompt: "Upbeat energetic synth-pop song about a summer romance, female vocals, catchy chorus, 80s inspired production, 128 BPM",
      audio: "https://musicfile.kie.ai/2025/1/13/2a2a0de04f1df91a5bd8e154f24bec21.mp3",
      credits: 12
    },
    {
      id: 2,
      style: "Lo-Fi",
      title: t("กาแฟยามบ่าย", "Afternoon Coffee"),
      thumbnail: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800",
      prompt: "Chill lo-fi hip hop beats for studying, jazzy piano chords, soft vinyl crackle, atmospheric rain sounds in background, no vocals",
      audio: "https://musicfile.kie.ai/2025/1/13/46ce7df28e3b4a24f00115c54a72d7f8.mp3",
      credits: 12
    },
    {
      id: 3,
      style: "Cinematic",
      title: t("การเดินทางที่ยิ่งใหญ่", "The Great Journey"),
      thumbnail: "https://images.unsplash.com/photo-1460881680858-30d872d5b530?w=800",
      prompt: "Epic cinematic orchestral soundtrack, building intensity, powerful brass and soaring strings, Hans Zimmer style, adventurous mood",
      audio: "https://musicfile.kie.ai/2025/1/13/4198444a703d2e3badfe75421a22495b.mp3",
      credits: 12
    },
    {
      id: 4,
      style: "Rock",
      title: t("พายุอารมณ์", "Emotional Storm"),
      thumbnail: "https://images.unsplash.com/photo-1459749411177-042180ce673c?w=800",
      prompt: "Modern alternative rock song, distorted electric guitars, powerful drums, male vocals, raw emotional energy, high energy bridge",
      audio: "https://musicfile.kie.ai/2025/1/13/d41ca496df87d2efd43486ec22137682.mp3",
      credits: 12
    },
    {
      id: 5,
      style: "EDM",
      title: t("แสงไฟนีออน", "Neon Lights"),
      thumbnail: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=800",
      prompt: "Modern future bass EDM track, heavy bass drops, melodic synth leads, ethereal vocal chops, party vibe, 150 BPM",
      audio: "https://musicfile.kie.ai/2025/1/13/2c6686311ce838a6a6828b051b747377.mp3",
      credits: 12
    },
    {
      id: 6,
      style: "Jazz",
      title: t("ค่ำคืนในกรุงเทพ", "Midnight in Bangkok"),
      thumbnail: "https://images.unsplash.com/photo-1511192336575-5a79af67a629?w=800",
      prompt: "Smooth late night jazz, smoky saxophone solo, walking bassline, subtle brushed drums, lounge atmosphere, sophisticated mood",
      audio: "https://musicfile.kie.ai/2025/1/13/b16e8b4e724a73602f067d02dc2c7482.mp3",
      credits: 12
    }
  ];

  const copyPrompt = (prompt: string, id: string) => {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(id);
    toast.success(t("คัดลอก Prompt แล้ว!", "Prompt copied!"));
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const tryPrompt = (prompt: string, type: "photo" | "video" | "music") => {
    const encodedPrompt = encodeURIComponent(prompt);
    window.location.href = `/generate/${type}?prompt=${encodedPrompt}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 pt-24 pb-16">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">
            <Sparkles className="h-3 w-3 mr-1" />
            {t("ตัวอย่างผลงาน", "Examples Gallery")}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            {t("ดูตัวอย่างผลงานคุณภาพสูง", "High-Quality Examples")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t(
              "ผลงานที่สร้างจาก AI Models ชั้นนำ พร้อม Prompts ที่ใช้จริง",
              "Created with leading AI models, with real prompts included"
            )}
          </p>
        </div>

        {/* Subscription Promo Banner */}
        <Card variant="glow" className="mb-12 overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-secondary/20 p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-2xl bg-primary/20 border border-primary/30">
                    <Gift className="h-10 w-10 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Crown className="h-5 w-5 text-yellow-500" />
                      <span className="text-sm font-medium text-yellow-500">
                        {t("โปรโมชั่นพิเศษ", "Special Offer")}
                      </span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold mb-1">
                      {t("เฉพาะลูกค้า Ebook Aievergreen แถมพิเศษ 200 เครดิต", "Exclusive for Ebook Aievergreen Customers: Bonus 200 Credits")}
                    </h3>
                    <p className="text-muted-foreground">
                      {t(
                        "เมื่อสมัคร Package 1,999 บาท",
                        "When you subscribe to the ฿1,999 package"
                      )}
                    </p>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button variant="glow" size="lg" className="gap-2 whitespace-nowrap">
                    <Zap className="h-5 w-5" />
                    {t("ดูแพ็คเกจ", "View Plans")}
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <Badge variant="outline" className="bg-background/50">
                  ✨ {t("Prompts สำหรับภาพ Portrait", "Portrait Prompts")}
                </Badge>
                <Badge variant="outline" className="bg-background/50">
                  🎬 {t("Prompts สำหรับวิดีโอ Cinematic", "Cinematic Video Prompts")}
                </Badge>
                <Badge variant="outline" className="bg-background/50">
                  🎨 {t("Prompts สไตล์ Anime", "Anime Style Prompts")}
                </Badge>
                <Badge variant="outline" className="bg-background/50">
                  🏙️ {t("Prompts สถาปัตยกรรม", "Architecture Prompts")}
                </Badge>
                <Badge variant="outline" className="bg-background/50">
                  🍜 {t("Prompts Food Photography", "Food Photography Prompts")}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Video/Image/Music */}
        <Tabs defaultValue="video" className="w-full">
          <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3 mb-8">
            <TabsTrigger value="video" className="gap-2 text-sm md:text-base">
              <Play className="h-4 w-4" />
              {t("วิดีโอ", "Videos")}
            </TabsTrigger>
            <TabsTrigger value="image" className="gap-2 text-sm md:text-base">
              <ImageIcon className="h-4 w-4" />
              {t("ภาพ", "Images")}
            </TabsTrigger>
            <TabsTrigger value="music" className="gap-2 text-sm md:text-base">
              <Music className="h-4 w-4" />
              {t("เพลง", "Music")}
            </TabsTrigger>
          </TabsList>

          {/* Video Examples */}
          <TabsContent value="video">
            <div className="grid md:grid-cols-2 gap-6">
              {videoExamples.map((video) => (
                <Card key={video.id} variant="glass" className="overflow-hidden group">
                  <div className="relative aspect-video overflow-hidden bg-black">
                    <video
                      src={video.video}
                      className="w-full h-full object-cover"
                      controls
                      loop
                      muted
                      playsInline
                      preload="metadata"
                    />
                    <Badge className="absolute top-3 left-3 bg-primary z-10 pointer-events-none">
                      {video.model}
                    </Badge>
                    <Badge variant="secondary" className="absolute top-3 right-3 z-10 pointer-events-none">
                      {video.duration}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{video.title}</h3>
                      <span className="text-sm text-muted-foreground">
                        {video.credits} {t("เครดิต", "credits")}
                      </span>
                    </div>
                    <div className="relative mb-3">
                      <p className="text-sm text-muted-foreground line-clamp-3 pr-8">
                        {video.prompt}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-0 right-0 h-8 w-8"
                        onClick={() => copyPrompt(video.prompt, `video-${video.id}`)}
                      >
                        {copiedPrompt === `video-${video.id}` ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      variant="glow"
                      size="sm"
                      className="w-full gap-2"
                      onClick={() => tryPrompt(video.prompt, "video")}
                    >
                      <ArrowRight className="h-4 w-4" />
                      {t("ลองใช้ Prompt นี้", "Try this prompt")}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Image Examples */}
          <TabsContent value="image">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {imageExamples.map((img) => (
                <Card key={img.id} variant="glass" className="overflow-hidden group">
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={img.image}
                      alt={img.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <Badge className="absolute top-3 left-3 bg-primary">
                      {img.model}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-sm">{img.title}</h3>
                      <span className="text-xs text-muted-foreground">
                        {img.credits} {t("เครดิต", "credits")}
                      </span>
                    </div>
                    <div className="relative mb-3">
                      <p className="text-xs text-muted-foreground line-clamp-3 pr-8">
                        {img.prompt}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-0 right-0 h-7 w-7"
                        onClick={() => copyPrompt(img.prompt, `img-${img.id}`)}
                      >
                        {copiedPrompt === `img-${img.id}` ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <Button
                      variant="glow"
                      size="sm"
                      className="w-full gap-2 text-xs"
                      onClick={() => tryPrompt(img.prompt, "photo")}
                    >
                      <ArrowRight className="h-3 w-3" />
                      {t("ลองใช้ Prompt นี้", "Try this prompt")}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Music Examples */}
          <TabsContent value="music">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {musicExamples.map((music) => (
                <Card key={music.id} variant="glass" className="overflow-hidden group">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={music.thumbnail}
                      alt={music.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="p-4 rounded-full bg-primary/80">
                        <Music className="h-8 w-8 text-primary-foreground" />
                      </div>
                    </div>
                    <Badge className="absolute top-3 left-3 bg-primary">
                      {music.style}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{music.title}</h3>
                      <span className="text-sm text-muted-foreground">
                        {music.credits} {t("เครดิต", "credits")}
                      </span>
                    </div>
                    <div className="relative mb-3">
                      <p className="text-sm text-muted-foreground line-clamp-3 pr-8">
                        {music.prompt}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-0 right-0 h-8 w-8"
                        onClick={() => copyPrompt(music.prompt, `music-${music.id}`)}
                      >
                        {copiedPrompt === `music-${music.id}` ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    {/* Audio Player */}
                    <div className="mb-3 bg-muted/20 rounded-md p-2">
                      <audio controls className="w-full h-8" src={music.audio} preload="none"></audio>
                    </div>

                    <Button
                      variant="glow"
                      size="sm"
                      className="w-full gap-2"
                      onClick={() => tryPrompt(music.prompt, "music")}
                    >
                      <ArrowRight className="h-4 w-4" />
                      {t("ลองใช้ Prompt นี้", "Try this prompt")}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {t("พร้อมสร้างผลงานของคุณเอง?", "Ready to create your own?")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t(
              "เริ่มต้นสร้างภาพและวิดีโอคุณภาพสูงด้วย AI วันนี้",
              "Start creating high-quality images and videos with AI today"
            )}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/generate">
              <Button variant="glow" size="lg" className="gap-2">
                <Sparkles className="h-5 w-5" />
                {t("เริ่มสร้างเลย", "Start Creating")}
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" size="lg">
                {t("ดูราคา", "View Pricing")}
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Example;
